import React, { useState, useEffect } from 'react';
import { useOfflineSync, useCache } from '../hooks/useOffline';
import { cacheManager } from '../services/cacheManager';
import { 
  Cloud, CloudOff, RefreshCw, Database, CheckCircle, 
  AlertCircle, Clock, Loader2, Download, Trash2, X,
  Package, MapPin, BookOpen, ClipboardList, Layers
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { toast } from 'sonner';

// Sync Status Badge - shows pending actions count
export const SyncStatusBadge = ({ onClick }) => {
  const { pendingCount, isSyncing, isOnline } = useOfflineSync();

  if (pendingCount === 0 && isOnline) return null;

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors hover:bg-slate-100"
    >
      {!isOnline ? (
        <>
          <CloudOff className="w-4 h-4 text-amber-500" />
          <span className="text-amber-600">Hors ligne</span>
        </>
      ) : isSyncing ? (
        <>
          <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
          <span className="text-blue-600">Synchronisation...</span>
        </>
      ) : pendingCount > 0 ? (
        <>
          <Cloud className="w-4 h-4 text-amber-500" />
          <span className="text-amber-600">{pendingCount} en attente</span>
        </>
      ) : null}
    </button>
  );
};

// Freshness Indicator - shows if data is from cache
export const FreshnessIndicator = ({ cacheInfo, onRefresh, className = '' }) => {
  if (!cacheInfo) return null;

  const { fromCache, age, isStale } = cacheInfo;

  if (!fromCache && !isStale) {
    return (
      <div className={`flex items-center gap-1.5 text-xs text-emerald-600 ${className}`}>
        <CheckCircle className="w-3.5 h-3.5" />
        <span>Données à jour</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className={`flex items-center gap-1.5 text-xs ${isStale ? 'text-amber-600' : 'text-slate-500'}`}>
        <Clock className="w-3.5 h-3.5" />
        <span>{fromCache ? `Cache: ${age}` : age}</span>
        {isStale && (
          <Badge variant="outline" className="text-xs px-1.5 py-0 text-amber-600 border-amber-300">
            Obsolète
          </Badge>
        )}
      </div>
      {onRefresh && (
        <button
          onClick={onRefresh}
          className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          title="Actualiser"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

// Cache Status Panel - full view of cache state
export const CacheStatusPanel = ({ open, onClose }) => {
  const { cacheFreshness, isPreCaching, cacheProgress, preCacheAll, clearCache, refreshStale } = useCache();
  const { pendingCount, pendingActions, isSyncing, isOnline, syncNow } = useOfflineSync();
  const [clearing, setClearing] = useState(false);

  const handlePreCache = async () => {
    toast.info('Mise en cache des données...');
    await preCacheAll();
    toast.success('Données mises en cache');
  };

  const handleSync = async () => {
    const result = await syncNow();
    if (result) {
      if (result.synced > 0) {
        toast.success(`${result.synced} action(s) synchronisée(s)`);
      }
      if (result.failed > 0) {
        toast.error(`${result.failed} action(s) échouée(s)`);
      }
    }
  };

  const handleClearCache = async () => {
    setClearing(true);
    await clearCache();
    setClearing(false);
    toast.success('Cache vidé');
  };

  const cacheItems = Object.entries(cacheFreshness);
  const cachedCount = cacheItems.filter(([, info]) => info.isCached).length;
  const staleCount = cacheItems.filter(([, info]) => info.isStale && info.isCached).length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-500" />
            Gestion hors-ligne
          </DialogTitle>
        </DialogHeader>

        {/* Connection Status */}
        <div className={`flex items-center gap-3 p-3 rounded-lg ${isOnline ? 'bg-emerald-50' : 'bg-amber-50'}`}>
          {isOnline ? (
            <>
              <Cloud className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="font-medium text-emerald-700">Connecté</p>
                <p className="text-xs text-emerald-600">Synchronisation automatique activée</p>
              </div>
            </>
          ) : (
            <>
              <CloudOff className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-700">Hors ligne</p>
                <p className="text-xs text-amber-600">Les actions seront synchronisées au retour en ligne</p>
              </div>
            </>
          )}
        </div>

        {/* Pending Actions */}
        {pendingCount > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm">Actions en attente</h4>
              <Badge variant="secondary">{pendingCount}</Badge>
            </div>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {pendingActions.slice(0, 5).map((action, i) => (
                <div key={action.id || i} className="flex items-center gap-2 text-xs text-slate-600 p-2 bg-slate-50 rounded">
                  <Clock className="w-3 h-3" />
                  <span className="flex-1 truncate">{action.type}</span>
                  <span className="text-slate-400">
                    {new Date(action.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ))}
              {pendingCount > 5 && (
                <p className="text-xs text-slate-400 text-center">
                  +{pendingCount - 5} autre(s)
                </p>
              )}
            </div>
            <Button 
              onClick={handleSync} 
              disabled={!isOnline || isSyncing}
              size="sm"
              className="w-full"
            >
              {isSyncing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Synchronisation...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Synchroniser maintenant
                </>
              )}
            </Button>
          </div>
        )}

        {/* Cache Status */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">Données en cache</h4>
            <span className="text-xs text-slate-500">
              {cachedCount}/{cacheItems.length} endpoints
              {staleCount > 0 && ` • ${staleCount} obsolète(s)`}
            </span>
          </div>

          {/* Pre-cache progress */}
          {isPreCaching && cacheProgress && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-slate-600">
                <span>Mise en cache: {cacheProgress.label}</span>
                <span>{cacheProgress.completed}/{cacheProgress.total}</span>
              </div>
              <Progress value={(cacheProgress.completed / cacheProgress.total) * 100} />
            </div>
          )}

          {/* Cache items list */}
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {cacheItems.map(([endpoint, info]) => (
              <div 
                key={endpoint}
                className="flex items-center justify-between p-2 rounded bg-slate-50 text-sm"
              >
                <div className="flex items-center gap-2">
                  {info.isCached ? (
                    <CheckCircle className={`w-4 h-4 ${info.isStale ? 'text-amber-500' : 'text-emerald-500'}`} />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-slate-300" />
                  )}
                  <span className={info.isCached ? 'text-slate-700' : 'text-slate-400'}>
                    {info.label}
                  </span>
                  {info.itemCount > 0 && (
                    <Badge variant="outline" className="text-xs">
                      {info.itemCount}
                    </Badge>
                  )}
                </div>
                <span className={`text-xs ${info.isStale ? 'text-amber-600' : 'text-slate-400'}`}>
                  {info.age}
                </span>
              </div>
            ))}
          </div>

          {/* Cache actions */}
          <div className="flex gap-2">
            <Button 
              onClick={handlePreCache} 
              disabled={isPreCaching || !isOnline}
              size="sm"
              className="flex-1"
            >
              {isPreCaching ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  En cours...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Tout mettre en cache
                </>
              )}
            </Button>
            <Button 
              onClick={handleClearCache}
              disabled={clearing}
              variant="outline"
              size="sm"
            >
              {clearing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Tips */}
        <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
          <p className="font-medium mb-1">Conseils :</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Mettez en cache avant de passer hors-ligne</li>
            <li>Les scans sont sauvegardés localement</li>
            <li>La synchronisation est automatique au retour en ligne</li>
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Floating offline indicator with sync status
export const OfflineSyncIndicator = () => {
  const [showPanel, setShowPanel] = useState(false);
  const { pendingCount, isOnline, isSyncing } = useOfflineSync();

  const showIndicator = !isOnline || pendingCount > 0 || isSyncing;

  if (!showIndicator) return null;

  return (
    <>
      <button
        onClick={() => setShowPanel(true)}
        className={`fixed bottom-20 left-4 z-40 flex items-center gap-2 px-4 py-2 rounded-full shadow-lg transition-all hover:scale-105 ${
          !isOnline 
            ? 'bg-amber-500 text-white' 
            : isSyncing 
              ? 'bg-blue-500 text-white'
              : 'bg-white text-slate-700 border border-slate-200'
        }`}
      >
        {!isOnline ? (
          <>
            <CloudOff className="w-4 h-4" />
            <span className="text-sm font-medium">Hors ligne</span>
            {pendingCount > 0 && (
              <Badge className="bg-white/20 text-white">{pendingCount}</Badge>
            )}
          </>
        ) : isSyncing ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm font-medium">Sync...</span>
          </>
        ) : (
          <>
            <Cloud className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-medium">{pendingCount} en attente</span>
          </>
        )}
      </button>

      <CacheStatusPanel open={showPanel} onClose={() => setShowPanel(false)} />
    </>
  );
};

// Cache Status Bar - Shows cached items count per category
// Icons for each cache type
const CACHE_ICONS = {
  '/api/furniture-types': Layers,
  '/api/catalog': BookOpen,
  '/api/locations/sites': MapPin,
  '/api/furniture': Package,
  '/api/inventory': ClipboardList
};

const CACHE_SHORT_LABELS = {
  '/api/furniture-types': 'Types',
  '/api/catalog': 'Catalogue',
  '/api/locations/sites': 'Sites',
  '/api/furniture': 'Mobilier',
  '/api/inventory': 'Inventaires'
};

export const CacheStatusBar = ({ className = '' }) => {
  const [cacheStats, setCacheStats] = useState({});
  const [loading, setLoading] = useState(true);
  const { isOnline } = useOfflineSync();

  useEffect(() => {
    loadCacheStats();
    
    // Refresh stats periodically
    const interval = setInterval(loadCacheStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadCacheStats = async () => {
    try {
      const freshness = await cacheManager.getCacheFreshness();
      setCacheStats(freshness);
    } catch (error) {
      console.error('Error loading cache stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalCached = Object.values(cacheStats).reduce((sum, item) => sum + (item.itemCount || 0), 0);
  const cachedEndpoints = Object.values(cacheStats).filter(item => item.isCached).length;
  const totalEndpoints = Object.keys(cacheStats).length;

  if (loading || totalEndpoints === 0) return null;

  return (
    <TooltipProvider>
      <div className={`flex items-center gap-1 ${className}`} data-testid="cache-status-bar">
        {/* Compact indicator */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-default ${
              cachedEndpoints === totalEndpoints 
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                : cachedEndpoints > 0 
                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                  : 'bg-slate-50 text-slate-500 border border-slate-200'
            }`}>
              <Database className="w-3.5 h-3.5" />
              <span>{totalCached} en cache</span>
              {!isOnline && <CloudOff className="w-3 h-3 text-amber-500" />}
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="p-0">
            <div className="p-3 space-y-2 min-w-[200px]">
              <div className="flex items-center justify-between text-xs font-medium border-b pb-2 mb-2">
                <span>Données en cache</span>
                <span className={cachedEndpoints === totalEndpoints ? 'text-emerald-600' : 'text-amber-600'}>
                  {cachedEndpoints}/{totalEndpoints}
                </span>
              </div>
              {Object.entries(cacheStats).map(([endpoint, info]) => {
                const Icon = CACHE_ICONS[endpoint] || Database;
                const label = CACHE_SHORT_LABELS[endpoint] || info.label;
                return (
                  <div key={endpoint} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-3.5 h-3.5 ${info.isCached ? 'text-emerald-500' : 'text-slate-300'}`} />
                      <span className={info.isCached ? 'text-slate-700' : 'text-slate-400'}>{label}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {info.isCached ? (
                        <>
                          <span className="font-mono font-medium text-slate-600">{info.itemCount}</span>
                          {info.isStale && (
                            <AlertCircle className="w-3 h-3 text-amber-500" title="Données obsolètes" />
                          )}
                        </>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {!isOnline && (
                <div className="flex items-center gap-1.5 text-xs text-amber-600 pt-2 border-t mt-2">
                  <CloudOff className="w-3 h-3" />
                  <span>Mode hors-ligne actif</span>
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>

        {/* Individual badges for quick view */}
        <div className="hidden sm:flex items-center gap-1">
          {Object.entries(cacheStats).map(([endpoint, info]) => {
            if (!info.isCached || info.itemCount === 0) return null;
            const Icon = CACHE_ICONS[endpoint] || Database;
            const label = CACHE_SHORT_LABELS[endpoint] || info.label;
            return (
              <Tooltip key={endpoint}>
                <TooltipTrigger asChild>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                    info.isStale 
                      ? 'bg-amber-50 text-amber-700' 
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    <Icon className="w-3 h-3" />
                    <span className="font-mono">{info.itemCount}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">
                    {label}: {info.itemCount} élément{info.itemCount > 1 ? 's' : ''}
                    {info.isStale && ' (obsolète)'}
                  </p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default {
  SyncStatusBadge,
  FreshnessIndicator,
  CacheStatusPanel,
  OfflineSyncIndicator,
  CacheStatusBar
};
